import { createContext, useState, useCallback, ReactNode, useContext } from 'react';

export interface UserInfo {
  loggedIn: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
  access_token?: string;
}

interface AuthContextShape {
  user: UserInfo;
  login: (userDetails: Partial<UserInfo>) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfo>(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      try {
        return JSON.parse(storedUser);
      } catch {
        return { loggedIn: false };
      }
    }
    return { loggedIn: false };
  });

  const login = useCallback((userDetails: Partial<UserInfo>) => {
    const { access_token, ...safeDetails } = userDetails;
    const userWithFlag: UserInfo = { ...safeDetails, loggedIn: true } as UserInfo;
    setUser(userWithFlag);
    localStorage.setItem('user', JSON.stringify(userWithFlag));
  }, []);

  const logout = useCallback(async () => {
    setUser({ loggedIn: false });
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }, []);

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
